/**
 * Provides an interface for modifying a form question.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * model | The `Field` associated with this view.
 * collection | The array of all `Field` models associated with the form.
 * @module app/views/dataType/DataFieldEditor
 */
import Backbone from "backbone";
import _ from "underscore";
import DataFieldValuesEditor from "app/views/layer/DataFieldValuesEditor";
import FormInputView from "core/views/FormInputView";
import Field from "core/models/Field";
import ButtonView from "core/views/ButtonView";
import ToggleInput from "core/views/ToggleInput";
import viewTemplate from "views/layer/DataFieldEditor";
import s from "underscore.string";
import FieldTypes from "core/models/FieldTypes";

export default Backbone.View.extend({
  template: viewTemplate,

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {core/models/Field} options.model The field associated with this view.
   * @param {app/models/FieldCollection} options.fields The set of fields in which the field model resides.
   */
  initialize: function (options) {
    this.options = options;

    this.model = this.options.model;
    this.fields = this.options.fields;
  },

  tagName: "li",
  className: "question-item well data-field-editor",

  render: function () {
    var self = this;
    self.dataFieldValuesEditor = new DataFieldValuesEditor({
      model: this.model,
      fields: this.fields,
    });

    this.$el.html(
      this.template(
        _.defaults({}, this.options, {
          canRemove: true,
        })
      )
    );

    var labelInput = new FormInputView({
      model: this.model,
      field: new Field(Field.fields["label"]),
    });
    var categoryInput = new FormInputView({
      model: this.model,
      field: new Field(Field.fields["category"]),
    });
    var publicFieldTypes = {
      Check: {
        value: FieldTypes.Check,
        text: "Check Box List",
      },
      CheckBox: {
        value: FieldTypes.CheckBox,
        text: "Single Check Box",
      },
      Radio: {
        value: FieldTypes.Radio,
      },
      Select: {
        value: FieldTypes.Select,
      },
      Date: {
        value: FieldTypes.Date,
      },
      //Link: {
      // value: FieldTypes.Link
      //},
      //standard input types:
      Number: {
        value: FieldTypes.Number,
      },
      Email: {
        value: FieldTypes.Email,
      },
      Phone: {
        value: FieldTypes.Phone,
      },
      Text: {
        value: FieldTypes.Text,
      },
      TextArea: {
        value: FieldTypes.TextArea,
        text: "Multi-Line Text",
      },
      Url: {
        value: FieldTypes.Url,
        text: "URL",
      },
    };
    var typeInput = new FormInputView({
      model: this.model,
      field: new Field(
        _.extend({}, Field.fields["type"], {
          values: _.map(publicFieldTypes, function (value, text) {
            return {
              value: value.value,
              text: value.text || s.humanize(text),
            };
          }),
        })
      ),
    });

    var newToggleView = new ToggleInput({
      model: this.model,
    });

    var filterableInput = new ButtonView({
      toggleable: true,
      click: function () {
        self.model.set("filterable", filterableInput.isActive());
      },
      text: "",
      icon: "filter",
      className: "btn-primary",
      tooltip: "Filterable",
      attributes: {
        tabindex: "-1",
      },
    });
    var sortableInput = new ButtonView({
      toggleable: true,
      click: function () {
        self.model.set("sortable", sortableInput.isActive());
      },
      text: "",
      icon: "sort",
      className: "btn-primary",
      tooltip: "Sortable",
      attributes: {
        tabindex: "-1",
      },
    });
    var listableInput = new ButtonView({
      toggleable: true,
      click: function () {
        self.model.set("listable", listableInput.isActive());
      },
      text: "",
      icon: "list",
      className: "btn-primary",
      tooltip: "Listable",
      attributes: {
        tabindex: "-1",
      },
    });
    var editableInput = new ButtonView({
      toggleable: true,
      click: function () {
        self.model.set("editable", editableInput.isActive());
      },
      text: "",
      icon: "lock",
      className: "btn-primary",
      tooltip: "Editable",
      attributes: {
        tabindex: "-1",
      },
    });

    var addRule = new ButtonView({
      click: function () {
        self.dataFieldValuesEditor.addRule();
      },
      text: "",
      icon: "plus",
      className: "btn-primary btn-add",
      tooltip: "Add Rule",
      attributes: {
        tabindex: "-1",
      },
    });

    var deleteInput = new ButtonView({
      click: function () {
        var id = self.model.get("id");
        self.removeFieldFromRules(id);
        self.fields.remove(self.model);
      },
      text: "",
      icon: "trash",
      className: "btn-danger",
      tooltip: "Delete Field",
      attributes: {
        tabindex: "-1",
      },
    });

    labelInput.render();
    typeInput.render();
    categoryInput.render();

    filterableInput.render();
    sortableInput.render();
    listableInput.render();
    editableInput.render();
    addRule.render();
    deleteInput.render();
    newToggleView.render();

    if (this.model.get("filterable")) {
      filterableInput.activate();
    } else {
      filterableInput.deactivate();
    }

    if (this.model.get("sortable")) {
      sortableInput.activate();
    } else {
      sortableInput.deactivate();
    }

    if (this.model.get("listable")) {
      listableInput.activate();
    } else {
      listableInput.deactivate();
    }

    if (this.model.get("filterable")) {
      editableInput.activate();
    } else {
      editableInput.deactivate();
    }

    this.$(".field-text").append(labelInput.$el);
    this.$(".field-type").append(typeInput.$el);
    this.$(".field-category").append(categoryInput.$el);
    this.$(".field-toggles")
      .append(filterableInput.$el)
      .append(sortableInput.$el)
      .append(listableInput.$el)
      .append(editableInput.$el)
      .append(addRule.$el)
      .append(deleteInput.$el);

    this.$(".clsToggle").html(newToggleView.$el);

    if (this.model.get("type") == "text") {
      this.$(".btn-add").hide();
    }

    if (this.model.hasValues()) {
      self.dataFieldValuesEditor.render();
      self.dataFieldValuesEditor.$el.appendTo(this.$(".options-container"));
    }
    this.$el[0].setAttribute("data-id", this.model.id);
    this.model.on("change:visible", function () {
      var visible = self.model.get("visible");
      var id = self.model.get("id");
      if (visible == true) {
        self.removeFieldFromRules(id);
      } else {
        self.addFieldToRules(id);
      }
    });
    this.model.on("change:label", function () {
      var visible = self.model.get("visible");
      var id = self.model.get("id");
      var label = self.model.get("label");
      if (visible == false) {
        self.changeFieldLabelRules(id, label);
      }
    });
    this.model.once("change:type", this.render, this);
  },
  removeFieldFromRules: function (id) {
    var self = this;
    $("select#ruleField").each(function (index, selectControl) {
      if (selectControl.value == id) {
        var valueId = $(selectControl)
          .closest("[id^='divValueRules']")
          .attr("id");
        var fieldId = $(selectControl)
          .closest(".data-field-editor")
          .attr("data-id");
        var field = self.fields.filter(function (f) {
          return f.id == fieldId;
        });
        if (field.length > 0) {
          field[0].removeValueRule(id, valueId.replace("divValueRules", ""));
        }
        $(selectControl).closest(".divVisibleField").remove();
      }
    });
    $("#ruleField option[value='" + id + "']").remove();
  },
  addFieldToRules: function (id) {
    var self = this;
    $("select#ruleField").each(function (index, selectControl) {
      var fieldId = $(selectControl)
        .closest(".data-field-editor")
        .attr("data-id");
      if (fieldId != id) {
        var field = self.fields.filter(function (f) {
          return f.id == id;
        });
        if (field.length > 0) {
          $(selectControl).append(
            "<option value='" +
              field[0].get("id") +
              "'>" +
              field[0].get("label") +
              "</option>"
          );
        }
      }
    });
  },
  changeFieldLabelRules: function (id, label) {
    $("select#ruleField option[value='" + id + "']").html(label);
  },
  /**
   * Sets focus to the field of this view associated with the field text.
   */
  focus: function () {
    var $el = this.$(".field-text input");
    var text = $el.focus().val();

    $el.val("").val(text);
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/layer/DataFieldEditor.js
// module id = 886
// module chunks = 1